import React, { FC, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { once } from 'lodash'

import HardwareAnalysis from './hardware-test/HardwareAnalysis'
import HardwareTestRuns from './HardwareTestRuns'
import { useHardwareTestRunData } from '../../../hooks/use-hardware-test-run-data'
import { useTriggerHardwareTest } from 'src/Libs/installation-details/hooks/use-trigger-hardware-test'
import { TestRunRecord } from 'src/Libs/installation-details/models/hardware-tests.model'
import { useRunningHardwareTest } from 'src/Libs/installation-details/hooks/use-running-hardware-test'
import { TriggerHardwareTestParamsData, TestRunsModel, HardwareTestResultModel } from '../../../models'
import { cancelTest } from 'src/Libs/installation-details/api/hardware-tests.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { usePendingTests } from 'src/Libs/installation-details/hooks/use-pending-tests'

/**
 * Root component to dispay info about hardware tests within the system.
 */
const HardwareTests: FC = () => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { storageId } = useParams<{ storageId: string }>()
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [runningTest, setRunningTest] = useState<TestRunRecord | HardwareTestResultModel>()
  const [testIdToAnalyse, setTestIdToAnalyse] = useState<string | undefined>(undefined)
  const [pendingTest, , , resetPendingTest] = usePendingTests(storageId)

  const checkRunningTest = once((resultData: TestRunsModel | null) => {
    const runningTest = resultData?.data.find(test => test.state === 'RUNNING')

    if (runningTest) {
      setRunningTest(runningTest)
    }
  })

  // for the initial test
  const { runningHardwareTestResult, finishTesting: finishInitialHardwareTest } = useRunningHardwareTest(storageId, runningTest?.testRunId)

  // for the further tests
  const { hardwareTestResult, triggerTest, isTriggered, finishTesting: finishHardwareTest } = useTriggerHardwareTest(storageId, testIdToAnalyse)

  const finishTesting = hardwareTestResult.data ? finishHardwareTest : finishInitialHardwareTest

  const setInitialDataAndFinishTest = (): void => {
    finishTesting()
    setRunningTest(undefined)
    setTestIdToAnalyse(undefined)
  }

  const [testRunsData, refetchTestRunsData, testRunsDataLoading] = useHardwareTestRunData({
    pageSize,
    offset: page * pageSize,
    storageId: Number(storageId),
    onSuccess: checkRunningTest,
  })

  const testResultData = hardwareTestResult.data ?? runningHardwareTestResult.data ?? pendingTest
  const isConnected = hardwareTestResult.connected || runningHardwareTestResult.connected

  const onTriggerTest = (testId: string, params?: TriggerHardwareTestParamsData): void => {
    setTestIdToAnalyse(testId)
    triggerTest(params)
  }

  const onRerunTask = async (testRun: TestRunRecord): Promise<void> => {
    onTriggerTest(testRun.testId, testRun.params)
  }

  const finishTest = (): void => {
    const testRunId = testResultData?.testRunId
    const initialTestRunId = runningTest?.testRunId
    const testState = testResultData?.state

    if (testState) {
      setInitialDataAndFinishTest()
      resetPendingTest()
      return
    }

    // We can cancel triggered or initial test
    const testRunIdForCancel = (testRunId ?? initialTestRunId) as string

    cancelTest(storageId, testRunIdForCancel, authFetch)
      .then(() => {
        enqueueSuccessStack(t('system-details.hardware-test.cancel-success'))
      })
      .catch(() => {
        enqueueErrorStack(t('system-details.hardware-test.cancel-failed'))
      })
  }

  useEffect(() => {
    if (testResultData?.state) {
      refetchTestRunsData()
    }
  }, [testResultData?.state, refetchTestRunsData])

  return (
    <>
      <HardwareTestRuns
        data={testRunsData}
        dataLoading={testRunsDataLoading}
        page={page}
        pageSize={pageSize}
        triggerHardwareTest={triggerTest}
        setPage={setPage}
        setPageSize={setPageSize}
        isTriggered={isTriggered || !!testResultData || isConnected}
        onRunTask={onRerunTask}
      />
      {(isTriggered || !!testResultData || isConnected) && (
        <HardwareAnalysis testResultData={testResultData} onTriggerTest={onTriggerTest} finishTest={finishTest} />
      )}
    </>
  )
}

export default HardwareTests
